import {
  DestroyRef,
  Directive,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';

declare global {
  interface Document {
    mozCancelFullScreen?: () => Promise<void>;
    msExitFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
    mozFullScreenElement?: Element;
    msFullscreenElement?: Element;
    webkitFullscreenElement?: Element;
  }

  interface HTMLElement {
    msRequestFullScreen?: () => Promise<void>;
    mozRequestFullScreen?: () => Promise<void>;
    webkitRequestFullScreen?: () => Promise<void>;
  }
}

@Directive({
  selector: '[poziFullScreen]',
  exportAs: 'poziFullScreen',
})
export class FullScreenDirective {
  elementToSendFullScreen = input<HTMLElement>();

  /**
   * Whether `elementToSendFullScreen()` is currently the document's
   * fullscreen element. Hosts bind their button glyph to this — an
   * `innerHTML` write from a `document.on*` handler used to do it, which
   * retained the destroyed host element for the life of the page and broke
   * every instance but the last-clicked one when several were live.
   */
  readonly isFullscreen = signal(false);

  constructor() {
    const onFullscreenChange = () => this.isFullscreen.set(this.isFullScreen());
    // The prefixed trio must stay in lockstep with the fallback matrix in
    // isFullScreen/requestFullScreen/exitFullScreen (and GoogleMapsComponent's
    // HostListener set): a browser that takes a prefixed request path fires
    // only the prefixed change event, and the signal would never flip.
    const changeEvents = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange',
    ];
    for (const event of changeEvents) {
      document.addEventListener(event, onFullscreenChange);
    }
    inject(DestroyRef).onDestroy(() => {
      for (const event of changeEvents) {
        document.removeEventListener(event, onFullscreenChange);
      }
    });
  }

  @HostListener('click') onClick() {
    if (!this.elementToSendFullScreen()) {
      return;
    }
    if (this.isFullScreen()) {
      this.exitFullScreen();
    } else {
      this.requestFullScreen();
    }
  }

  isFullScreen(): boolean {
    return (
      (document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement) == this.elementToSendFullScreen()
    );
  }

  requestFullScreen() {
    const element = this.elementToSendFullScreen();
    if (!element) {
      return;
    }
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.webkitRequestFullScreen) {
      element.webkitRequestFullScreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.msRequestFullScreen) {
      element.msRequestFullScreen();
    }
  }

  exitFullScreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }
  }
}
